import { useState, useEffect, useContext } from "react";
import { withApollo } from "react-apollo";
import { useSelector } from "react-redux";
import { OwcTypography, OwcInput } from "@one/react";
import { LIST_DIGITAL_LAB_BOOKING_USERS } from "../../gql/bookingapi/queries";
import { APPROVE_BOOKING, UPDATE_BOOKING_COMMAND } from "../../gql/bookingapi/mutations";
import ConfirmDialog from "../../components/shared/ConfirmDialog";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { formatUserDisplay } from "../../utils/helpers/text";
import { UPDATE_REQUEST } from "../../constants";
import { UserSelectDropdown } from "../../components/shared/UserSelectionComponents";

const AssistanceAssignmentDialog = ({
  client,
  open,
  onClose,
  booking,
  user,
  setStatus,
  isEditMode = false,
  onApprovalComplete
}) => {
  const { dispatchAction } = useContext(PaginationContext);
  const reduxUsers = useSelector((state) => state.users.users);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState("");
  const [selectedAssistantName, setSelectedAssistantName] = useState("");
  const [assistanceDuration, setAssistanceDuration] = useState("");

  const calculateBookingDuration = (dateFrom, dateTo) => {
    if (!dateFrom || !dateTo) return 1;
    const start = new Date(dateFrom);
    const end = new Date(dateTo);
    const durationMs = end - start;
    const durationHours = Math.max(0.5, Math.round(durationMs / (1000 * 60 * 60) * 2) / 2);
    return durationHours;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      // First check if we have users in Redux
      if (reduxUsers && reduxUsers.length > 0) {
        setUsers(reduxUsers);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await client.query({
          query: LIST_DIGITAL_LAB_BOOKING_USERS,
          fetchPolicy: "network-only",
        });

        if (result?.data?.listDigitalLabBookingUsers?.items) {
          setUsers(result.data.listDigitalLabBookingUsers.items);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open, client, reduxUsers]);

  useEffect(() => {
    if (open && booking) {
      if (isEditMode && booking) {
        if (booking.assistentTime) {
          setAssistanceDuration(booking.assistentTime.toString());
        }
      } else if (!isEditMode) {
        const bookingDuration = calculateBookingDuration(booking.dateFrom, booking.dateTo);
        setAssistanceDuration(bookingDuration.toString());
      }

      if (users.length > 0 && (isEditMode || !booking.assistentTime)) {
        if (booking.assistent && booking.assistent[0]) {
          const assistantUser = users.find(user => user.userId === booking.assistent[0]);
          if (assistantUser) {
            setSelectedAssistant(assistantUser.userId);
            const displayName = formatUserDisplay(assistantUser);
            setSelectedAssistantName(displayName);
          } else {
            setSelectedAssistant(booking.assistent[0]);
            setSelectedAssistantName(`User ID: ${booking.assistent[0]}`);
          }
        }
      }
    }
  }, [open, booking, isEditMode, users]);

  const handleApprove = async () => {
    if (!booking?.id || !selectedAssistant || !assistanceDuration) return;

    try {
      setLoading(true);
      setStatus(true);
      dispatchAction({
        type: "fetching",
        payload: true,
      });

      if (isEditMode) {
        const entries = booking.bookingEntries?.map(entry => ({
          inventoryId: entry.inventoryId || entry.equipment?.inventoryId,
          bookingEntryId: entry.id,
          dateFrom: entry.dateFrom || booking.dateFrom,
          dateTo: entry.dateTo || booking.dateTo,
          allDay: entry.allDay || false,
          remark: booking.remark || '',
          commandType: UPDATE_REQUEST
        })) || [];

        await client.mutate({
          mutation: UPDATE_BOOKING_COMMAND,
          variables: {
            bookingId: booking.id,
            assistent: [selectedAssistant],
            assistentTime: parseFloat(assistanceDuration),
            createdBy: user.user,
            createdByEmail: user.email,
            createdByName: user.name || `${user.givenName || ''} ${user.familyName || ''}`.trim(),
            dateFrom: booking.dateFrom,
            dateTo: booking.dateTo,
            reservedForUsers: booking.reservedForUsers || booking.bookedUsers?.map(u => u.user || u.userId) || [],
            reservedForEmails: booking.reservedForEmails || booking.bookedUsers?.map(u => u.email) || [],
            type: booking.type || 'BOOKED',
            entries: entries,
            site: booking.site || user.site,
          },
        });
      } else {
        await client.mutate({
          mutation: APPROVE_BOOKING,
          variables: {
            bookingId: booking.id,
            approval: {
              status: "APPROVED",
              approvedBy: user.user,
              approvedByEmail: user.email,
              assistant: [selectedAssistant],
              assistantTime: parseFloat(assistanceDuration),
            },
            createdBy: user.user,
            createdByEmail: user.email,
          },
        });
      }

      // Call the callback to refresh the parent component
      if (onApprovalComplete) {
        onApprovalComplete();
      }
    } catch (error) {
      setLoading(false);
      setStatus(false);
      dispatchAction({
        type: "clearAll",
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedAssistant("");
    setSelectedAssistantName("");
    setAssistanceDuration("");
    // Clear loading state if dialog is closed
    if (loading) {
      setLoading(false);
      setStatus(false);
      dispatchAction({
        type: "clearAll",
      });
    }
    onClose();
  };

  const handleOnCancel = () => {
    setSelectedAssistant("");
    setSelectedAssistantName("");
    setAssistanceDuration("");
    if (loading) {
      setLoading(false);
      setStatus(false);
      dispatchAction({
        type: "clearAll",
      });
    }
    onClose();
  };

  return (
    <ConfirmDialog
      open={open}
      close={handleClose}
      onCancel={handleOnCancel}
      onApprove={handleApprove}
      title={isEditMode ? "Edit assistance and time" : "Approve Booking"}
      approveText={isEditMode ? "Save" : "Approve"}
      approveColor="primary"
      approveVariant="contained"
      cancelText="Cancel"
      cancelVariant="outlined"
      cancelColor="primary"
      loading={loading}
      disableBooking={!selectedAssistant || !assistanceDuration || (assistanceDuration && parseFloat(assistanceDuration) <= 0)}
      fullWidth={false}
      maxWidth="sm"
      data-testid="assistance-assignment-dialog"
    >
      <div style={{ padding: "0 1rem 1rem 0rem", display: "flex", flexDirection: "column" }}>
        {!isEditMode && (
          <div style={{ paddingBottom: "1rem" }}>
            <OwcTypography variant="button" style={{
              color: "#544F4F",
              paddingBottom: ".5rem",
              paddingTop: ".5rem"
            }}>
              Hazardous materials
            </OwcTypography>
            <OwcTypography variant="body1">
              {booking?.hazardousDeclaration?.hazardousMaterialsList || "-"}
            </OwcTypography>
          </div>
        )}

        <div style={{ gap: "1rem", display: "flex", flexDirection: "column", flexGrow: 1 }}>
          <div>
            <OwcTypography variant="button" style={{
              color: "#544F4F",
              paddingBottom: ".5rem",
              paddingTop: ".5rem"
            }}>
              Assistance
            </OwcTypography>
            <UserSelectDropdown
              required={true}
              name="assistantName"
              style={{ width: "100%" }}
              label="Select assistant"
              value={selectedAssistantName || null}
              key={selectedAssistantName + booking?.assistanceDeclaration?.assistanceReason}
              data-testid="assistant-select"
              optionsConfig={{ includeAllUsers: false }}
              onChange={(selectedOption, _userData) => {
                if (selectedOption && selectedOption.name) {
                  setSelectedAssistant(selectedOption.name);
                  setSelectedAssistantName(selectedOption.label);
                } else {
                  // Clear selection when null is passed
                  setSelectedAssistant("");
                  setSelectedAssistantName("");
                }
              }}
              users={users}
              placeholder="Select assistant"
              returnUserData={true}
              keyExtractor={(option, index) => option.userId || option.name || index}
            />

          </div>

          <div style={{ width: "100%", flexGrow: 1 }}>
            <OwcInput
              type={"number"}
              required={true}
              style={{ width: "100%" }}
              label="Planned assistant time"
              value={assistanceDuration}
              min="0.5"
              step="0.5"
              placeholder="Enter duration in hours"
              onInputChange={(e) => {
                setAssistanceDuration(e.target?.value);
              }}
            >
              <div slot="suffix">
                h
              </div>
            </OwcInput>
          </div>
        </div>

        <div style={{ padding: ".5rem 1rem 1rem 0rem" }}>
          <OwcTypography variant="button" style={{
            color: "#544F4F",
            paddingBottom: ".5rem",
            paddingTop: ".5rem"
          }}>
            Assistance reason
          </OwcTypography>
          <OwcTypography variant="body1">
            {booking?.assistanceDeclaration?.assistanceReason || "-"}
          </OwcTypography>
        </div>
      </div>
    </ConfirmDialog>
  );
};

export default withApollo(AssistanceAssignmentDialog);
